import type { AIModel, ReasoningSummarySupport } from './types.js';

/**
 * Capability catalogue for *provider-exposed processing explanations*.
 *
 * A model is marked `supported` only when its vendor documents that the
 * streaming API returns user-visible reasoning summaries / reasoning content
 * **with the request payload we already send** (no extra opt-in options, no
 * extended-thinking budgets, no additional billed calls) *and* the matching
 * adapter already normalises those fields into `thinking` chunks.
 *
 * Everything else is `unsupported`; providers whose wire format we do not model
 * at all resolve to `unknown`, which callers must treat as unsupported.
 *
 * Deliberately excluded:
 * - Anthropic Claude — `thinking_delta` requires enabling extended thinking
 *   (`thinking: { type: 'enabled', budget_tokens }`), which changes the output
 *   token budget/billing and constrains sampling options
 *   (https://platform.claude.com/docs/en/build-with-claude/extended-thinking).
 * - Google Gemini — thought summaries require `thinkingConfig.includeThoughts`
 *   and thought tokens are billed (https://ai.google.dev/gemini-api/docs/thinking).
 * - OpenAI — `/chat/completions` (the endpoint this platform uses) does not
 *   return reasoning summaries; they are exclusive to the Responses API
 *   (`reasoning: { summary }`).
 */
interface ProviderReasoningPolicy {
  /** Model-code patterns whose streams expose explanations out of the box. */
  supported: RegExp[];
}

const PROVIDER_REASONING_POLICIES: Record<string, ProviderReasoningPolicy> = {
  // DeepSeek reasoning models stream `delta.reasoning_content` by default
  // (https://api-docs.deepseek.com/guides/reasoning_model); `deepseek-chat`
  // does not.
  deepseek: { supported: [/^deepseek-reasoner\b/i, /^deepseek-r1\b/i] },
  // xAI documents `reasoning_content` for the Grok mini reasoning models only;
  // Grok 4 returns no readable reasoning on /chat/completions
  // (https://docs.x.ai/developers/model-capabilities/text/reasoning).
  xai: { supported: [/^grok-3-mini\b/i] },
  // Moonshot returns `reasoning_content` for its thinking models only; the
  // instruct/agentic Kimi models do not
  // (https://platform.kimi.ai/docs/guide/use-kimi-k2-thinking-model).
  moonshot: { supported: [/thinking/i] },
  // Known wire formats without out-of-the-box explanations.
  openai: { supported: [] },
  anthropic: { supported: [] },
  google: { supported: [] },
  microsoft: { supported: [] },
};

function normalize(modelCode: string): string {
  return modelCode.trim().replace(/^models\//, '');
}

/**
 * Resolve the capability from authoritative provider/adapter metadata.
 *
 * @returns `unknown` when the provider wire format is not modelled here, so a
 * failed or missing check never masquerades as a definitive `unsupported`.
 */
export function resolveReasoningSummarySupport(
  providerCode: string,
  modelCode: string,
): ReasoningSummarySupport {
  const policy = PROVIDER_REASONING_POLICIES[providerCode.trim().toLowerCase()];
  if (!policy) return 'unknown';
  const code = normalize(modelCode);
  if (code.length === 0) return 'unknown';
  return policy.supported.some((pattern) => pattern.test(code)) ? 'supported' : 'unsupported';
}

/** Conservative read of the stored capability: only `supported` enables the UI. */
export function exposesReasoningSummary(
  model: Pick<AIModel, 'reasoningSummarySupport'> | null | undefined,
): boolean {
  return model?.reasoningSummarySupport === 'supported';
}
